import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  fetchCurrentUser,
  signInWithGoogleIdToken,
  signOut as apiSignOut,
} from "../services/authApi";

const AuthContext = createContext({
  user: null,
  loading: true,
  isLoggedIn: false,
  signInWithGoogle: async () => {},
  signOut: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const me = await fetchCurrentUser();
        if (!cancelled) setUser(me);
      } catch (err) {
        console.warn("Failed to load current user:", err);
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signInWithGoogle = useCallback(async (idToken) => {
    const { user: signedInUser, is_new_user } =
      await signInWithGoogleIdToken(idToken);
    setUser(signedInUser);
    return { user: signedInUser, isNewUser: Boolean(is_new_user) };
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiSignOut();
    } finally {
      setUser(null);
    }
  }, []);

  const value = {
    user,
    loading,
    isLoggedIn: !!user,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
